import {DatePipe} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MetaProgresso, MetaRequest, MetaResponse, TIPO_META_CONFIG, TipoMeta, TipoMetaConfig} from '@core/models/meta';
import {EnumsService} from '@core/services/enums.service';
import {MetaService} from '@core/services/meta.service';
import {environment} from '@environments/environment';
import {ConfirmationService, MessageService} from 'primeng/api';
import {Button} from 'primeng/button';
import {Checkbox} from 'primeng/checkbox';
import {ConfirmDialog} from 'primeng/confirmdialog';
import {DatePicker} from 'primeng/datepicker';
import {Dialog} from 'primeng/dialog';
import {Fluid} from 'primeng/fluid';
import {InputNumber} from 'primeng/inputnumber';
import {InputText} from 'primeng/inputtext';
import {MultiSelect} from 'primeng/multiselect';
import {Select} from 'primeng/select';
import {Skeleton} from 'primeng/skeleton';
import {Tag} from 'primeng/tag';
import {Textarea} from 'primeng/textarea';
import {TableModule} from 'primeng/table';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {MetaProgressoPanel} from '@shared/components/meta-progresso-panel/meta-progresso-panel';
import {MetaValorPipe} from '@shared/pipes/meta-valor.pipe';
import {cursoAlvoValidoValidator, dataFimAposDataInicioValidator, statusAlvoValidoValidator} from './admin-metas.validators';

@Component({
  selector: 'app-admin-metas',
  imports: [
    Button, Dialog, Fluid, InputText, InputNumber, DatePicker,
    MultiSelect, Select, Checkbox, Skeleton, Tag, DatePipe,
    ReactiveFormsModule, Textarea, ConfirmDialog, TableModule,
    IconField, InputIcon, MetaProgressoPanel, MetaValorPipe,
  ],
  templateUrl: './admin-metas.html',
})
export class AdminMetas implements OnInit {
  metas: MetaResponse[] = [];
  loading = true;
  showDialog = false;
  saving = false;
  editingId: number | null = null;
  form!: FormGroup;
  statusOptions: {label: string; value: string}[] = [];
  statusAlvoOptions: {label: string; value: string}[] = [];
  cursoOptions: {label: string; value: string}[] = [];
  tipoMetaOptions: TipoMetaConfig[] = Object.values(TIPO_META_CONFIG);

  progressoDialogVisible = false;
  progressoSelecionado: MetaProgresso | null = null;
  progressoLoading = false;

  private service = inject(MetaService);
  private enumsService = inject(EnumsService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private fb = inject(FormBuilder);

  ngOnInit() {
    this.initForm();
    this.loadStatus();
    this.loadCursos();
    this.loadMetas();
  }

  get tipoAtual(): TipoMeta {
    return this.form.get('tipoMeta')?.value ?? 'VALOR';
  }

  get tipoConfigAtual(): TipoMetaConfig {
    return TIPO_META_CONFIG[this.tipoAtual];
  }

  tipoConfigDe(tipo: TipoMeta): TipoMetaConfig {
    return TIPO_META_CONFIG[tipo];
  }

  private initForm() {
    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.maxLength(100)]],
      descricao: [''],
      valorMeta: [null, Validators.required],
      dataInicio: [null, Validators.required],
      dataFim: [null, Validators.required],
      statusContribuintes: [[], Validators.required],
      ativo: [true],
      tipoMeta: ['VALOR', Validators.required],
      statusAlvo: [null],
      cursoAlvo: [null],
    }, {validators: [dataFimAposDataInicioValidator, statusAlvoValidoValidator, cursoAlvoValidoValidator]});

    this.form.get('statusContribuintes')!.valueChanges.subscribe((lista: string[]) => {
      this.recalcularStatusAlvoOptions(lista);
      const statusAlvo = this.form.get('statusAlvo')!.value;
      if (statusAlvo && !lista.includes(statusAlvo)) {
        this.form.get('statusAlvo')!.setValue(null);
      }
    });

    this.form.get('tipoMeta')!.valueChanges.subscribe((tipo: TipoMeta) => {
      if (tipo !== 'PERCENTUAL_STATUS') {
        this.form.get('statusAlvo')!.setValue(null, {emitEvent: false});
      }
      if (tipo !== 'PROCESSOS_PENDENTES') {
        this.form.get('cursoAlvo')!.setValue(null, {emitEvent: false});
      }
    });
  }

  private recalcularStatusAlvoOptions(statusContribuintes: string[]) {
    this.statusAlvoOptions = (statusContribuintes ?? []).map(s => ({label: s, value: s}));
  }

  private loadCursos() {
    if ((environment as any).local) {
      this.cursoOptions = [
        {label: 'ORDINÁRIO', value: 'ORDINÁRIO'},
        {label: 'QNL', value: 'QNL'},
      ];
      return;
    }
    this.enumsService.getCursoEnum().subscribe(list => {
      this.cursoOptions = list.map(c => ({label: c.nome, value: c.nome}));
    });
  }

  private loadStatus() {
    if ((environment as any).local) {
      this.statusOptions = [
        {label: 'EM ANÁLISE', value: 'EM ANÁLISE'},
        {label: 'TAP', value: 'TAP'},
        {label: 'ARBITRAL', value: 'ARBITRAL'},
        {label: 'ACOLHIDA', value: 'ACOLHIDA'},
        {label: 'CANCELADA', value: 'CANCELADA'},
        {label: 'ANULADA', value: 'ANULADA'},
      ];
      return;
    }
    this.enumsService.getStatusEnum().subscribe(list => {
      this.statusOptions = list.map(s => ({label: s.nome, value: s.nome}));
    });
  }

  private loadMetas() {
    this.loading = true;
    this.service.listar().subscribe({
      next: data => { this.metas = data; this.loading = false; },
      error: () => this.loading = false,
    });
  }

  abrirNova() {
    this.editingId = null;
    this.form.reset({ativo: true, statusContribuintes: [], tipoMeta: 'VALOR', statusAlvo: null, cursoAlvo: null});
    this.recalcularStatusAlvoOptions([]);
    this.showDialog = true;
  }

  editar(meta: MetaResponse) {
    this.editingId = meta.id;
    this.form.patchValue({
      ...meta,
      dataInicio: new Date(meta.dataInicio + 'T00:00:00'),
      dataFim: new Date(meta.dataFim + 'T00:00:00'),
    });
    this.recalcularStatusAlvoOptions(meta.statusContribuintes);
    this.showDialog = true;
  }

  salvar() {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    const v = this.form.value;
    const dto: MetaRequest = {
      ...v,
      dataInicio: this.toDateStr(v.dataInicio),
      dataFim: this.toDateStr(v.dataFim),
    };
    const req = this.editingId
      ? this.service.atualizar(this.editingId, dto)
      : this.service.criar(dto);
    req.subscribe({
      next: () => {
        this.messageService.add({severity: 'success', summary: 'Salvo', detail: 'Meta salva com sucesso.'});
        this.showDialog = false;
        this.loadMetas();
        this.saving = false;
      },
      error: () => { this.saving = false; }
    });
  }

  confirmarExclusao(meta: MetaResponse) {
    this.confirmationService.confirm({
      header: 'Confirmar remoção',
      message: `Remover a meta "${meta.nome}"? Esta ação não pode ser desfeita.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonProps: {severity: 'danger', label: 'Remover'},
      rejectButtonProps: {severity: 'secondary', outlined: true, label: 'Cancelar'},
      accept: () => this.deletar(meta.id),
    });
  }

  private deletar(id: number) {
    this.service.deletar(id).subscribe({
      next: () => {
        this.messageService.add({severity: 'success', summary: 'Removida', detail: 'Meta removida.'});
        this.loadMetas();
      }
    });
  }

  visualizarProgresso(meta: MetaResponse) {
    this.progressoDialogVisible = true;
    this.progressoSelecionado = null;
    this.progressoLoading = true;
    this.service.getProgresso(meta.id).subscribe({
      next: p => { this.progressoSelecionado = p; this.progressoLoading = false; },
      error: () => this.progressoLoading = false,
    });
  }

  private toDateStr(d: Date): string {
    return d.toISOString().split('T')[0];
  }
}
